import React from "react";
import Overlay from "../Overlay";
import Select from "react-select";
import { sweetalert } from "../../App";
import FAQAPI from "../../network/FAQAPI";
import ProviderAPI from "../../network/ProviderAPI";
import AdminAPI from "../../network/AdminAPI";
import SimpleTable from "../tables/SimpleTable";
import { Column } from "../tables/TableBase";
import { Sorter, TableOrder } from "../../sorting/Sorter";
import {
  getNumPages,
  getPageOptions,
  getSelectLabels,
  slicePages,
} from "../../util/FormatUtil";
import { SubmittingProvider } from "../../types/SubmittingProvider";

const ITEMS_PER_PAGE = 25;

interface CompendiumState {
  showLoading: boolean;
  facilities: any[];
  facilityLabels: { label; value }[];
  selectedFacilityID: number | null;
  facilityProviders: SubmittingProvider[];
  providerSearch: string;
  tableData: SubmittingProvider[];
  filteredCount: number;
  page_options: { label; value }[];
  selected_page: { label; value };
  searched: boolean;
  sortKey: string | null;
  sortDirection: TableOrder;
  direction?: TableOrder;
}

export class Compendium extends React.Component<any, CompendiumState> {

  constructor(props) {
    super(props);
    this.state = {
      showLoading: false,
      facilities: [],
      facilityLabels: [],
      selectedFacilityID: null,
      facilityProviders: [],
      providerSearch: "",
      tableData: [],
      filteredCount: 0,
      page_options: [{ label: "1", value: "1" }],
      selected_page: { label: "1", value: 1 },
      searched: false,
      sortKey: null,
      sortDirection: "asc",
      direction: "asc",
    };
    this.downloadCompendium = this.downloadCompendium.bind(this);
    this.refreshProviderTable = this.refreshProviderTable.bind(this);
  }

  componentDidMount() {
    document.title = 'Compendium';
    this.loadFacilities();
  }

  loadFacilities() {
    this.setState({ showLoading: true }, async () => {
      try {
        let data = await AdminAPI.getFacilitiesForSubmissionForm();
        let facilities = data.authorizedFacilities || [];
        this.setState({
          facilities,
          facilityLabels: getSelectLabels(facilities),
          showLoading: false,
        }, () => {
          // Auto-select when the user has exactly one facility
          if (facilities.length === 1) {
            this.handleFacilityChange(facilities[0].ID);
          }
        });
      } catch (e) {
        console.error(e);
        this.setState({ showLoading: false });
      }
    });
  }

  handleFacilityChange(facilityID: number) {
    this.setState({ selectedFacilityID: facilityID, providerSearch: "" }, () => {
      this.loadProvidersForFacility(facilityID);
    });
  }

  loadProvidersForFacility(facilityID: number) {
    this.setState({ showLoading: true }, async () => {
      try {
        let res = await ProviderAPI.filterProviders({
          filter: { FullName: [], NPI_Number: [], ContactID: [], FacilityID: [facilityID] }
        });
        let list = res && res.success && res.data && res.data[0] ? res.data[0] : [];
        this.setState({ facilityProviders: list, showLoading: false }, () => this.refreshProviderTable(1));
      } catch (e) {
        console.error(e);
        this.setState({ facilityProviders: [], showLoading: false }, () => this.refreshProviderTable(1));
      }
    });
  }

  refreshProviderTable(page: number) {
    let list = [...this.state.facilityProviders];
    let search = this.state.providerSearch.trim().toLowerCase();
    if (search) {
      list = list.filter(p =>
        (p.FullName || "").toLowerCase().includes(search) ||
        String(p.NPI_Number || "").toLowerCase().includes(search) ||
        String(p.ContactID || "").toLowerCase().includes(search)
      );
    }
    // Sort the full result set before slicing, otherwise only the visible page gets ordered
    if (this.state.sortKey) {
      let sorter = new Sorter<any>();
      list = sorter.sortByKey(list, this.state.sortKey as keyof any, this.state.sortDirection);
    }
    this.setState({
      tableData: slicePages(list, page, ITEMS_PER_PAGE),
      filteredCount: list.length,
      searched: search.length > 0,
      page_options: getPageOptions(getNumPages(list, ITEMS_PER_PAGE)),
      selected_page: { label: String(page), value: page },
    });
  }

  changePage(page: number) {
    this.refreshProviderTable(page);
  }

  useSorter(col: Column) {
    let appliedDirection = this.state.direction;
    this.setState({
      sortKey: col.key as string,
      sortDirection: appliedDirection,
      direction: appliedDirection === "asc" ? "desc" : "asc",
    }, () => this.refreshProviderTable(this.state.selected_page.value));
  }

  downloadCompendium() {
    this.setState({ showLoading: true }, async () => {
      let succeeded = false;
      try {
        succeeded = await FAQAPI.downloadCompendium();
      } catch (e) {
        console.error(e);
      }
      this.setState({ showLoading: false }, () => {
        if (!succeeded) {
          sweetalert.fire({ icon: "error", title: "", text: "Unable to download the Compendium at this time." });
        }
      });
    });
  }

  renderCompendiumCard() {
    return (
      <div className="card mb-2 h-100">
        <div className="card-header verlag-bold">
          <h3>Compendium</h3>
        </div>
        <div className="card-body d-flex flex-column">
          <p>
            The Compendium is a spreadsheet reference for interacting with Maine-HETL's HL7 API.
          </p>
          <div className="text-center mt-auto">
            <button
              className="btn immySubmitButtonOutline"
              onClick={this.downloadCompendium}
            >
              Download Compendium
            </button>
          </div>
        </div>
      </div>
    );
  }

  renderProviderControls() {
    const singleFacility = this.state.facilities.length === 1;

    return (
      <div className="card mb-2 h-100">
        <div className="card-header verlag-bold">
          <h3>Providers</h3>
        </div>
        <div className="card-body">
          {this.state.facilities.length === 0 &&
            <p className="m-0">There are no facilities associated with your account.</p>
          }

          {this.state.facilities.length > 0 &&
            <div className="form-group row">
              <label className="col-12 col-md-3 col-form-label" htmlFor="facilitySelect">Submitter</label>
              <div className="col-12 col-md-9">
                <Select
                  inputId="facilitySelect"
                  isSearchable={true}
                  isDisabled={singleFacility}
                  placeholder={"Select a submitter..."}
                  noOptionsMessage={() => "No submitters"}
                  value={this.state.facilityLabels.find(f => f.value === this.state.selectedFacilityID) || null}
                  onChange={(e) => this.handleFacilityChange(e.value)}
                  className={"state_select"}
                  options={this.state.facilityLabels}
                />
              </div>
            </div>
          }

          <div className="form-group row mb-0">
            <label className="col-12 col-md-3 col-form-label" htmlFor="providerSearch">Search Providers</label>
            <div className="col-12 col-md-9">
              <input
                id="providerSearch"
                className="form-control"
                type="search"
                autoComplete="off"
                disabled={!this.state.selectedFacilityID}
                placeholder={this.state.selectedFacilityID ? "Search by name or NPI number" : "Please choose a submitter"}
                value={this.state.providerSearch}
                onChange={(e) => this.setState({ providerSearch: e.target.value }, () => this.refreshProviderTable(1))}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderProviderTable() {
    if (!this.state.selectedFacilityID) return null;

    if (this.state.tableData.length === 0) {
      if (this.state.showLoading) return null;
      return (
        <div className="card mt-2" style={{ marginBottom: '4rem' }}>
          <div className="card-body">
            <p className="m-0">
              {this.state.searched
                ? "No providers match your search."
                : "No providers found for this submitter."}
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="card mt-2" style={{ marginBottom: '4rem' }}>
        <div className="card-header verlag-bold stickToTop">
          <h4 className="text-center text-md-left">
            Providers
            <section className="tableHeaderSection float-md-right d-flex justify-content-around">
              <h4 className={"float-md-right"} aria-label="Total Records" role="alert">
                Total: {this.state.filteredCount}
              </h4>
              <h4 className="d-inline-block float-right align-middle pr-2 ml-5">Page </h4>
              <div className="align-middle float-right pages">
                <Select
                  isSearchable={true}
                  placeholder={"1"}
                  noOptionsMessage={() => "No option"}
                  value={this.state.selected_page}
                  aria-label="Table Page Number"
                  onChange={(e: { label; value }) =>
                    this.setState({ selected_page: e }, () => this.changePage(e.value))
                  }
                  className={"state_select"}
                  options={this.state.page_options}
                />
              </div>
            </section>
          </h4>
        </div>
        <div className="card-body p-0 m-0 table-responsive">
          <SimpleTable
            columns={[
              {
                label: "Provider",
                key: "FullName",
                popoverText: "Click to Sort By Provider Name",
              },
              {
                label: "NPI Number",
                key: "NPI_Number",
                popoverText: "Click to Sort By NPI Number",
              },
              {
                label: "Contact ID",
                key: "ContactID",
                popoverText: "Click to Sort By Contact ID",
              },
            ]}
            table_data={this.state.tableData}
            columnClickedCallback={(col) => this.useSorter(col)}
          />
        </div>
      </div>
    );
  }

  render() {
    return (
      <React.Fragment>
        <Overlay show_loading={this.state.showLoading} />
        <div className="container-fluid">
          <main id="main-content" tabIndex={-1} aria-label="Compendium">
            <div className="row">
              <div className="col-12 col-lg-6 pt-2">
                {this.renderProviderControls()}
              </div>
              <div className="col-12 col-lg-6 pt-2">
                {this.renderCompendiumCard()}
              </div>
            </div>
            <div className="row">
              <div className="col-12">
                {this.renderProviderTable()}
              </div>
            </div>
          </main>
        </div>
      </React.Fragment>
    );
  }
}
